import { useState } from "react";
import { HookCard } from "@features/tools/components/hooks/HookCard";
import { useGetLessonHook } from "@packages/api/madlenAPI";
import { LessonHookResponseDto } from "@packages/api/madlenAPI.schemas";
import { Expand, Sparkles, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import Text from "@core/text";
import { Button } from "@core/index";
import SpinnerLoader from "../components/SpinnerLoader";

const LessonHooksDetailComponent = ({
  tool,
  isImageLoading,
  closeModal,
}: {
  tool: LessonHookResponseDto;
  isImageLoading: boolean;
  closeModal: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 z-[9999] overflow-hidden bg-black bg-opacity-75 dark:bg-opacity-90 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          closeModal();
        }
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <Text variant={"h1"}>{tool.title || t("hooks.title")}</Text>
          <Button
            onClick={closeModal}
            variant="solid"
            size="icon"
            rounded="full"
            bgColor="hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X size={24} className="text-gray-500 dark:text-gray-400" />
          </Button>
        </div>

        <div className="flex-1 overflow-auto p-6 modal-content">
          <HookCard isImageLoading={isImageLoading} lessonHook={tool} />
        </div>
      </div>
    </div>
  );
};

const LessonHooks = ({ id }: { id: number | null }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading, isError } = useGetLessonHook(id, {
    query: {
      refetchInterval: (data) => {
        if (data?.state?.data?.hooks && data.state.data.hooks.length > 0) {
          return false;
        }
        return 2000;
      },
      enabled: !!id,
      staleTime: Infinity,
      cacheTime: 1000 * 60 * 30,
    },
  });

  const isContentLoading = isLoading || !data?.hooks || data.hooks.length === 0;

  const handleExpandClick = () => {
    setExpanded(true);
  };

  const closeModal = () => {
    setExpanded(false);
  };

  if (isContentLoading) {
    return <SpinnerLoader />;
  }

  if (isError) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <h3 className="text-xl font-medium mb-4 text-red-500 dark:text-red-400">
          {t("hooks.error.unableToLoad")}
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {t("hooks.error.tryRegenerate")}
        </p>
      </div>
    );
  }

  return (
    <>
      {expanded && data && (
        <LessonHooksDetailComponent
          tool={{
            ...data,
            gradeLevel: data.gradeLevel === -1 ? 0 : data.gradeLevel,
          }}
          isImageLoading={isLoading}
          closeModal={closeModal}
        />
      )}

      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-6">
        <div className="flex w-full justify-between items-center">
          <h3 className="text-xl font-medium mb-4 flex items-center justify-between text-gray-900 dark:text-gray-100">
            <div>{t("hooks.title")}</div>
          </h3>
          <button
            className="flex items-center justify-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition duration-200"
            onClick={handleExpandClick}
          >
            <Expand size={18} className="text-gray-400 dark:text-gray-500" />
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
          {t("hooks.subtitle")}
        </p>

        <div className="space-y-6">
          <div className="space-y-4">
            <div className="grid gap-4">
              {data.hooks &&
                Array.isArray(data.hooks) &&
                data.hooks.slice(0, 2).map((hook, index) => (
                  <div key={index} className="space-y-4">
                    {hook.title && (
                      <div className="flex flex-row items-center gap-2 text-blue-600 dark:text-blue-400 rounded-xl">
                        <span className="p-2 rounded-xl bg-blue-500 dark:bg-blue-900/75 text-blue-500 dark:text-blue-500">
                          <Sparkles
                            size={18}
                            className="text-blue-50 dark:text-blue-100"
                          />
                        </span>
                        <h5 className="font-semibold text-lg">{hook.title}</h5>
                      </div>
                    )}
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6 space-y-4">
                      <div className="prose dark:prose-invert max-w-none">
                        <div className="leading-relaxed text-gray-800 dark:text-gray-200">
                          <Markdown>{hook.description}</Markdown>
                        </div>
                      </div>
                      {hook.expected_outcome && (
                        <div className="mt-4">
                          <h6 className="text-sm font-bold text-gray-800 dark:text-gray-200">
                            {t("hooks.expectedOutcome")}:
                          </h6>
                          <div className="mt-1 text-gray-700 dark:text-gray-300">
                            <Markdown className="prose dark:prose-invert">
                              {hook.expected_outcome}
                            </Markdown>
                          </div>
                        </div>
                      )}
                      {hook.materials_needed &&
                        hook.materials_needed !== "Yok" &&
                        hook.materials_needed !== "None" &&
                        hook.materials_needed !== "None." && (
                          <div className="mt-4">
                            <h6 className="text-sm font-bold text-gray-800 dark:text-gray-200">
                              {t("hooks.materialsNeeded")}:
                            </h6>
                            <div className="mt-1 text-gray-700 dark:text-gray-300">
                              {hook.materials_needed}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LessonHooks;
